/**
 * What the marked cards do, at the table.
 *
 * A rules page read once will not survive the third hand, and a standard deck
 * gives no clue - a jack looks exactly like a jack whether or not this game
 * has written on it. So the answer sits where the question gets asked.
 *
 * ## Built from the same map the cards are
 *
 * `cardMarks` is declared by the game and drawn on the cards by `Card`. This
 * reads the identical map, so the legend cannot promise something the deck
 * does not say. A legend written out by hand would be a second copy of the
 * rules, and the second copy is the one that goes stale.
 *
 * ## Folded away, and remembered
 *
 * It costs a line of vertical space on a phone, which is the thing that
 * screen has least of - T1 and T3 in `game-refinement.md` are both that
 * problem. So it folds, and the choice is remembered per device, the way the
 * mute setting is: somebody who knows the game should not have to dismiss it
 * at every table, and somebody learning should not have to find it again.
 *
 * Not in `localStorage` as a rule about the player - it is a preference about
 * this screen on this device, which is exactly what that store is for. See
 * `player-record.ts` for the things that belong to the account instead.
 */

import { useState } from 'react';
import { Icon } from '../render/Icon.js';
import type { CardMarks } from '../render/marks-context.js';

const KEY = 'cards.legend.open';

function remembered(): boolean {
  try {
    // Open unless told otherwise: a player who has never seen this game is
    // the one the legend exists for.
    return window.localStorage.getItem(KEY) !== 'false';
  } catch {
    return true;
  }
}

function remember(open: boolean): void {
  try {
    window.localStorage.setItem(KEY, open ? 'true' : 'false');
  } catch {
    /* private browsing; the setting simply will not persist */
  }
}

/**
 * The order ranks are shown in.
 *
 * Not the map's own key order, which is whatever the game happened to write.
 * A legend is read left to right and should not shuffle itself between games
 * or between deploys.
 */
const ORDER = ['A', '2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K'];

export function CardLegend({ marks }: { marks: CardMarks }) {
  const [open, setOpen] = useState(remembered);

  const ranks = Object.keys(marks).sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
  // Every game but one, and the strip should cost them nothing at all.
  if (ranks.length === 0) return null;

  return (
    <div className={open ? 'card-legend open' : 'card-legend'}>
      <button
        type="button"
        className="card-legend-toggle"
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => {
            remember(!was);
            return !was;
          });
        }}
      >
        <Icon name="info" />
        <span>{open ? 'Hide the marks' : 'What do the marks mean?'}</span>
      </button>

      {open ? (
        <ul className="card-legend-list">
          {ranks.map((rank) => (
            <li key={rank}>
              {/*
                A tiny struck-through rank, drawn the way the real card is.
                Showing the mark beside a plain letter would be explaining the
                crayon without using it.
              */}
              <span className="card-legend-rank" aria-hidden="true">
                {rank}
                <span className="card-legend-strike" />
              </span>
              <span className="card-legend-mark">{marks[rank]}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
